import { useState } from "react";
import type { SubjectDTO } from "../../shared/types";
import { ApiError, del, post } from "../lib/api";
import { navigate } from "../lib/router";
import { scopeName, typePath } from "../lib/scopes";
import { confirmDialog } from "./Dialog";
import { Button, ErrorText, shortDate } from "./ui";

const ARCHIVE_BODY = "Everything in it stays and can be read. No agent can start new work under it, and the wakes waiting under it are cancelled. You can unarchive it.";

/**
 * A scope's way out, the same rule as an agent's and a type's: one no run has happened under is deleted, with what was
 * put on it; one a run has happened under is archived instead, so its history is never lost.
 */
export function ScopeActions({ scope, onChanged }: { scope: SubjectDTO; onChanged: (scope: SubjectDTO) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = scopeName(scope);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const archive = () => run(async () => onChanged(await post<SubjectDTO>(`/api/subjects/${scope.id}/archive`)));
  const unarchive = () => run(async () => onChanged(await post<SubjectDTO>(`/api/subjects/${scope.id}/unarchive`)));
  const remove = () =>
    run(async () => {
      if (!(await confirmDialog({ title: `Delete ${name}?`, body: "Only a scope no run has happened under can be deleted. The agents put under it, its ids, and its memories, skills and kept files go with it, and it cannot be undone.", confirm: "Delete scope", danger: true }))) return;
      try {
        await del(`/api/subjects/${scope.id}`);
        navigate(typePath(scope.kind));
      } catch (err) {
        // A run has happened under it: say so, and offer the way that keeps it (already archived, it simply stays so).
        if (!(err instanceof ApiError) || err.code !== "has_runs") throw err;
        const ran = err.message.replace(/: archive it instead$/, "");
        if (scope.archivedAt) throw new Error(`${ran}, so it stays archived.`);
        const offer = await confirmDialog({ title: `${name} can't be deleted`, body: `${ran}. Archive it instead: ${ARCHIVE_BODY}`, confirm: "Archive instead" });
        if (offer) onChanged(await post<SubjectDTO>(`/api/subjects/${scope.id}/archive`));
      }
    });

  return (
    <div className="mt-3 space-y-2">
      {scope.archivedAt && (
        <div className="flex max-w-3xl flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-fill px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 text-muted">
            Archived {shortDate(scope.archivedAt)}. Everything in it is kept; no agent can start new work under it.
          </span>
          <Button size="sm" disabled={busy} onClick={unarchive}>Unarchive</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {!scope.archivedAt && (
          <Button
            size="sm"
            disabled={busy}
            onClick={async () => {
              if (await confirmDialog({ title: `Archive ${name}?`, body: ARCHIVE_BODY, confirm: "Archive" })) await archive();
            }}
          >
            Archive
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={busy} onClick={remove}>Delete</Button>
      </div>
      <ErrorText error={error} />
    </div>
  );
}
